import React, { useEffect, useState } from "react";
import Loading from "../../Common/Loading";

// Matches VB: Response.Redirect("EL_GeneralQuery.aspx", False) — used by Save, Cancel and Delete alike
const LIST_PAGE_URL = "/WebAdmin/Reports/EL_GeneralQuery";

const EL_SaveStructuredReport = () => {
  const params = new URLSearchParams(window.location.search);
  const id = params.get("ID");

  const [title, setTitle] = useState("");
  const [users, setUsers] = useState<string[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);

  const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;

  const [form, setForm] = useState({
    name: "",
    description: "",
    mainHead: "",
    timeout: "",
    whoCanView: "",
  });

  // Single overlay flag covers every async operation on this page —
  // initial loads, save, delete — same pattern as EL_GeneralQuery.
  const [loading, setLoading] = useState(false);

  // --- Effects ---

  // VB: ObjGrid is always constructed in Page_Load regardless of Add/Edit
  useEffect(() => {
    setLoading(true);
    fetch(`${API_BASE_URL}api/reports/users/`)
      .then((res) => res.json())
      .then((res) => setUsers(res.users || []))
      .catch(() => setUsers([]))
      .finally(() => setLoading(false));
  }, []);

  // VB: If QueryID = "" -> "Add Structured Report", else "Edit Structured Report"
  // and fn_PopulateTable() only runs "If IsPostBack = False"; the React equivalent
  // is simply "on mount / on id change".
  useEffect(() => {
    if (id) {
      setTitle("Edit Structured Report");
      setLoading(true);

      fetch(`${API_BASE_URL}api/reports/get/?id=${id}`)
        .then((res) => res.json())
        .then((res) => {
          if (res.data) {
            setForm({
              name: res.data.name ?? "",
              description: res.data.description ?? "",
              mainHead: res.data.mainHead ?? "",
              timeout: res.data.timeout != null ? String(res.data.timeout) : "",
              whoCanView: res.data.whoCanView ?? "",
            });
            setSelectedUsers(
              res.data.whoCanView
                ? res.data.whoCanView.split(",").filter((v: string) => v !== "")
                : []
            );
          }
        })
        .finally(() => setLoading(false));
    } else {
      setTitle("Add Structured Report");
    }
  }, [id]);

  // VB JS: fn_selectedvalues() rewrites txtWhoCanView.value from whatever is
  // currently checked in the grid, every time a checkbox is clicked.
  const toggleUser = (login: string) => {
    setSelectedUsers((prev) => {
      const next = prev.includes(login)
        ? prev.filter((u) => u !== login)
        : [...prev, login];
      setForm((f) => ({ ...f, whoCanView: next.join(",") }));
      return next;
    });
  };

  const allSelected = users.length > 0 && users.every((u) => selectedUsers.includes(u));
  const toggleSelectAll = () => {
    const next = allSelected ? [] : [...users];
    setSelectedUsers(next);
    setForm((f) => ({ ...f, whoCanView: next.join(",") }));
  };

  // VB: WhoCanView textbox is a normal, editable TextBox (not read-only) —
  // grid selection just overwrites it; the user can also type into it directly.
  const handleWhoCanViewChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((f) => ({ ...f, whoCanView: e.target.value }));
  };

  // VB: validation() — checks Name first, then MainHead, exact message text
  const validate = (): boolean => {
    if (form.name.trim() === "") {
      alert("Name is Blank");
      return false;
    }
    if (form.mainHead.trim() === "") {
      alert("Mainhead is Blank");
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;

    // VB: Dim Query As String = CStr(Session("TMP_SQRY"))
    const query = sessionStorage.getItem("TMP_SQRY") || "";

    // VB: If IsNumeric(txtQueryTime.Text) = False Then txtQueryTime.Text = "0"
    const timeoutIsNumeric = form.timeout.trim() !== "" && !isNaN(Number(form.timeout));
    const timeout = timeoutIsNumeric ? Number(form.timeout) : 0;

    setLoading(true);
    try {
      await fetch(`${API_BASE_URL}api/reports/save/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id,
          name: form.name.trim(),
          description: form.description.trim(),
          mainHead: form.mainHead.trim(),
          timeout,
          whoCanView: selectedUsers.length ? selectedUsers.join(",") : form.whoCanView.trim(),
          query,
        }),
      });

      sessionStorage.removeItem("TMP_SQRY");
      window.location.href = LIST_PAGE_URL; // same target as VB's Response.Redirect
    } catch (err) {
      setLoading(false);
      alert("Error saving report.");
    }
  };

  const handleCancel = () => {
    window.location.href = LIST_PAGE_URL;
  };

  // VB: gfn_ShowMessage("Do you want Delete the Record?", "Q", "Confirm Delete", fn_confdelete)
  const handleDelete = async () => {
    if (!window.confirm("Do you want Delete the Record?")) return;

    setLoading(true);
    try {
      await fetch(`${API_BASE_URL}api/reports/delete/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id }),
      });

      window.location.href = LIST_PAGE_URL; // same target as Save/Cancel
    } catch (err) {
      setLoading(false);
      alert("Error deleting report.");
    }
  };

  // --- Styles ---
  // Fixed column widths + a single border source (collapsed CSS borders only,
  // no native `border` attribute) so rows line up evenly regardless of label
  // or content length.
  const LABEL_WIDTH = 160;
  const FORM_WIDTH = 560;

  const styles = {
    page: { fontFamily: "Arial", fontSize: "12pt", color: "black", padding: "10px" },
    formTable: {
      margin: "20px auto 0",
      width: `${FORM_WIDTH}px`,
      fontFamily: "Arial",
      fontSize: "11pt",
      borderCollapse: "collapse" as const,
      tableLayout: "fixed" as const,
      border: "1px solid #999",
    },
    titleCell: {
      textAlign: "center" as const,
      fontFamily: "Arial",
      fontSize: "14pt",
      fontWeight: "bold" as const,
      backgroundColor: "teal",
      color: "white",
      padding: "8px",
      border: "1px solid #999",
    },
    labelCell: {
      width: `${LABEL_WIDTH}px`,
      padding: "8px 10px",
      border: "1px solid #999",
      // backgroundColor: "#f2f2f2",
      fontWeight: "bold" as const,
      verticalAlign: "middle" as const,
    },
    inputCell: {
      padding: "8px 10px",
      border: "1px solid #999",
      verticalAlign: "middle" as const,
    },
    textInput: {
      width: "100%",
      boxSizing: "border-box" as const,
      border: "1px solid #ccc",
      borderRadius: "3px",
      padding: "4px 6px",
      fontFamily: "Arial",
      fontSize: "11pt",
    },

    gridSection: { width: `${FORM_WIDTH}px`, margin: "16px auto 0" },
    gridLabel: { fontWeight: "bold" as const, marginBottom: "6px" },
    gridBox: {
      border: "1px solid #80a0ff",
      borderRadius: "3px",
      maxHeight: "255px",
      overflowY: "auto" as const,
      width: "100%",
      boxSizing: "border-box" as const,
    },
    gridTable: { width: "100%", borderCollapse: "collapse" as const },
    gridHeaderRow: { position: "sticky" as const, top: 0, backgroundColor: "#CFE2FF", zIndex: 1 },
    gridHeaderCell: {
      textAlign: "left" as const,
      padding: "6px 10px",
      fontWeight: "normal" as const,
      color: "#2859ac",
      borderBottom: "1px solid #80a0ff",
      borderRight:"1px solid #80a0ff"
    },
    gridCheckboxCell: { width: "36px", padding: "6px 10px", borderBottom: "1px solid #80a0ff", borderRight:"1px solid #80a0ff" },
    gridRowCell: { padding: "6px 10px", borderBottom: "1px solid #80a0ff" },
    gridEmpty: { padding: "12px", color: "#777", textAlign: "center" as const },

    btnBar: { textAlign: "center" as const, marginTop: "18px" },
    btn: {
      backgroundColor: "#119ECC",
      fontFamily: "Trebuchet MS",
      fontSize: "10pt",
      fontWeight: "bold" as const,
      color: "white",
      border: "solid 2px orange",
      height: "28px",
      minWidth: "80px",
      cursor: "pointer",
      marginLeft: "10px",
    },
  };

  return (
    <div style={styles.page}>
      {loading && <Loading />}

      <table style={styles.formTable}>
        <colgroup>
          <col style={{ width: `${LABEL_WIDTH}px` }} />
          <col />
        </colgroup>
        <tbody>
          <tr>
            <td colSpan={2} style={styles.titleCell}>
              {title}
            </td>
          </tr>
          <tr>
            <td style={styles.labelCell}>Name</td>
            <td style={styles.inputCell}>
              <input
                style={styles.textInput}
                maxLength={50}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </td>
          </tr>
          <tr>
            <td style={styles.labelCell}>Description</td>
            <td style={styles.inputCell}>
              <input
                style={styles.textInput}
                maxLength={1000}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </td>
          </tr>
          <tr>
            <td style={styles.labelCell}>MainHead</td>
            <td style={styles.inputCell}>
              <input
                style={styles.textInput}
                maxLength={50}
                value={form.mainHead}
                onChange={(e) => setForm({ ...form, mainHead: e.target.value })}
              />
            </td>
          </tr>
          <tr>
            <td style={styles.labelCell}>QueryTimeout</td>
            <td style={styles.inputCell}>
              <input
                style={{ ...styles.textInput, width: "80px" }}
                maxLength={3}
                value={form.timeout}
                onChange={(e) => setForm({ ...form, timeout: e.target.value })}
              />
            </td>
          </tr>
          <tr>
            <td style={styles.labelCell}>WhoCanView</td>
            <td style={styles.inputCell}>
              <input
                style={styles.textInput}
                maxLength={2000}
                value={form.whoCanView}
                onChange={handleWhoCanViewChange}
              />
            </td>
          </tr>
        </tbody>
      </table>

      {/* Inline replacement for ClsGrid — plain checkbox list of Logins */}
      <div style={styles.gridSection}>
        <div style={styles.gridLabel}>Select users who can view this report:</div>
        <div style={styles.gridBox}>
          <table style={styles.gridTable}>
            <thead>
              <tr style={styles.gridHeaderRow}>
                <th style={styles.gridHeaderCell}>
                  <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} />
                </th>
                <th style={styles.gridHeaderCell}>Login</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 && !loading && (
                <tr>
                  <td colSpan={2} style={styles.gridEmpty}>
                    No users found.
                  </td>
                </tr>
              )}
              {users.map((u) => (
                <tr key={u}>
                  <td style={styles.gridCheckboxCell}>
                    <input
                      type="checkbox"
                      checked={selectedUsers.includes(u)}
                      onChange={() => toggleUser(u)}
                    />
                  </td>
                  <td style={styles.gridRowCell}>{u}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div style={styles.btnBar}>
        <button style={styles.btn} onClick={handleSave} disabled={loading}>
          Save
        </button>
        <button style={styles.btn} onClick={handleCancel} disabled={loading}>
          Cancel
        </button>
        {id && (
          <button style={styles.btn} onClick={handleDelete} disabled={loading}>
            Delete
          </button>
        )}
      </div>
    </div>
  );
};

export default EL_SaveStructuredReport;